import { useState } from 'react';
import type { TrendTag } from '../../types';

/**
 * Filtering the trending list by what kind of story it is — and growing the
 * vocabulary when it turns out to be missing one.
 *
 * COUNTS DESCRIBE THE WINDOW, NOT THE SELECTION. The backend counts before it
 * filters, so picking "weather" does not zero every other chip and make the
 * filter look broken at the moment it works.
 *
 * Tags with no cards in the window are hidden rather than disabled: a greyed
 * chip claims a bucket exists and is empty, and on a feed that churns daily
 * "no crime stories today" is not something anybody needs a control for.
 *
 * THE WANTED ROW is the answer to "what if a category comes up that nobody
 * thought of". The model names the bucket it wished it had, those arrive here
 * counted, and one click makes it real. Nothing is promoted automatically — a
 * tag is a filter somebody has to understand, and a vocabulary that grows on
 * its own is the sprawl a closed list exists to avoid.
 */

/** Labels come from the server now; this is only the fallback for a tag the
 *  window has a count for but whose row did not come back. */
export function tagLabel(tag: string, known: TrendTag[] = []): string {
  return known.find(t => t.value === tag)?.label ?? tag;
}

export default function TagFilter({
  counts,
  available,
  wanted,
  selected,
  onChange,
  onPromote,
  onDismiss,
  onAdd,
  onManage,
}: {
  counts: Record<string, number>;
  available: TrendTag[];
  wanted: TrendTag[];
  selected: string[];
  onChange: (next: string[]) => void;
  onPromote: (tag: TrendTag) => void;
  onDismiss: (tag: TrendTag) => void;
  onAdd: (value: string) => void;
  onManage: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  // Busiest first: on a general-interest feed the big buckets are the ones
  // people want to hide, so they should be the easiest to reach.
  const tags = Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  const toggle = (tag: string) =>
    onChange(
      selected.includes(tag) ? selected.filter(t => t !== tag) : [...selected, tag],
    );

  const submit = () => {
    const value = draft.trim();
    if (!value) return;
    onAdd(value);
    setDraft('');
    setAdding(false);
  };

  if (tags.length === 0 && wanted.length === 0) return null;

  return (
    <div className="mb-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map(([tag, n]) => {
          const on = selected.includes(tag);
          return (
            <button
              key={tag}
              onClick={() => toggle(tag)}
              aria-pressed={on}
              className={`rounded-full px-2.5 py-0.5 text-xs transition-colors ${
                on
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {tagLabel(tag, available)} <span className="opacity-60">{n}</span>
            </button>
          );
        })}

        {adding ? (
          <span className="inline-flex items-center gap-1">
            <input
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') {
                  setAdding(false);
                  setDraft('');
                }
              }}
              placeholder="one word"
              aria-label="New tag"
              className="w-28 rounded-full border border-blue-400 px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-blue-600 dark:bg-gray-700 dark:text-gray-100"
            />
            <button
              onClick={submit}
              disabled={!draft.trim()}
              className="rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Add
            </button>
          </span>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 dark:border-gray-600 dark:text-gray-400"
            title="Add a bucket of your own"
          >
            + tag
          </button>
        )}

        {/* The way into the full list, including anything dismissed. Quiet,
            because it is a maintenance door, not a filter. */}
        <button
          onClick={onManage}
          className="ml-1 text-xs text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          Manage
        </button>

        {selected.length > 0 && (
          <button
            onClick={() => onChange([])}
            className="ml-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Clear
          </button>
        )}
      </div>

      {wanted.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span>Asked for, not in your list:</span>
          {wanted.map(tag => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
              title={tag.hint}
            >
              <span className="font-medium">{tag.label}</span>
              {/* WORDS, NOT "×3". The count sat next to a dismiss "✕" and the
                  row read as "NBA x1 add x" — two different multiplication
                  signs doing unrelated jobs, one a quantity and one a verb.
                  Spelling the count out and making the actions words leaves no
                  glyph to mistake for another.

                  The count is the argument: one model on one odd story is not
                  evidence, the same word on its fifth term is. */}
              <span className="opacity-70">
                asked {tag.times_suggested === 1 ? 'once' : `${tag.times_suggested} times`}
              </span>
              <button
                onClick={() => onPromote(tag)}
                className="font-medium underline hover:no-underline"
              >
                add
              </button>
              <button
                onClick={() => onDismiss(tag)}
                className="underline opacity-70 hover:opacity-100"
                aria-label={`Stop suggesting ${tag.label}`}
                title="Stop suggesting this. Reversible under Manage."
              >
                no
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
